// Matches a product's admin-typed printMethod free-text value against the
// set of real-world labels vendors/admins have used for laser-engraving
// methods, so the laser-engraving-preview module
// (services/laserEngravingPreviewService.ts) can recognize a design that
// should get the real material-aware rendered preview. Mirrors
// embossingPreview.ts's own EMBOSSING_METHOD_KEYWORDS matching convention
// exactly (see that file's comment for the full rationale on why a
// substring match, not an enum).
//
// EN + NL — the codebase's real vendor/product data already uses these
// exact strings: "Laser engraving"/"Laser" (real ImageSideConfig.printMethod
// and ProductConfig.printMethods rows, e.g. SKU CX1546: "Laser
// engraving,Pad printing"; SKU LT26402: "Digital Print,Laser") and
// "Lasergravering" (the Dutch translation — confirmed in
// backend/src/services/translationLookup.ts's own PFConcept method-code
// map, e.g. LA/LB/LC/LD/LE/LF/LUVD all -> 'Lasergravering', and its
// 'Laser engraving' -> 'Lasergravering' EN->NL translation entry). No
// "engrav"/"graveren"/"gravure"/"lasergraveren" hits were found in real
// seeded data at implementation time, but are kept as substring-match
// coverage for the broader family of EN/NL vendor spellings this same
// codebase already anticipates for embossing's own Dutch synonyms
// ("blinddruk"/"reliëf") — cheap to include, costs nothing if unmatched.
const LASER_ENGRAVING_METHOD_KEYWORDS = [
  'laser',
  'engrav',
  'graveren',
  'gravure',
  'lasergravure',
  'lasergravering',
  'lasergraveren',
];

export function isLaserEngravingPrintMethod(printMethod: string | null | undefined): boolean {
  if (!printMethod) return false;
  const lower = printMethod.toLowerCase();
  return LASER_ENGRAVING_METHOD_KEYWORDS.some(kw => lower.includes(kw));
}

// No deboss-style second keyword list needed here — unlike embossing/
// debossing (raised vs. pressed-in are two real, distinct polarities),
// laser engraving has no "raised" analogue: it's always a removal effect
// (material burned/ablated away), so there's no second polarity to match on.
// The material-DEPENDENT look (soft beveled relief vs. flat desaturated
// line-etch) is decided by laser_engrave_relief_lighting.py's own
// classify_material() from the real product photo, not from any
// print-method keyword.

export const LASER_ENGRAVING_PREVIEW_DESIGN_IMAGE_TYPE = 'laser_engraving_preview';

// Design.laserEngravingPreviewStatus int convention — mirrors
// EmbossingPreviewStatus (see schema.prisma's own comment on the column).
export const LaserEngravingPreviewStatus = {
  NONE:       0,
  QUEUED:     1,
  PROCESSING: 2,
  DONE:       3,
  FAILED:     4,
} as const;
export type LaserEngravingPreviewStatusValue = typeof LaserEngravingPreviewStatus[keyof typeof LaserEngravingPreviewStatus];
